import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Callback page to handle the redirect from Microsoft after login.
 * MSAL's handleRedirectPromise() is only ever called once, inside
 * AuthProvider's init effect (@/contexts/AuthContext.tsx). This page
 * just observes that result instead of calling MSAL itself, since a
 * second concurrent handleRedirectPromise() call throws
 * BrowserAuthError: interaction_in_progress.
 */
const Callback: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing"
  );
  const [message, setMessage] = useState("جاري معالجة تسجيل الدخول...");

  useEffect(() => {
    if (loading) return;

    if (isAuthenticated) {
      setStatus("success");
      setMessage("تم تسجيل الدخول بنجاح! جاري التوجيه...");
      const timer = setTimeout(() => {
        navigate("/", { replace: true });
      }, 1000);
      return () => clearTimeout(timer);
    }

    setStatus("error");
    setMessage("فشل في تسجيل الدخول. جاري التوجيه إلى صفحة الدخول...");
    const timer = setTimeout(() => {
      navigate("/login", { replace: true });
    }, 2000);
    return () => clearTimeout(timer);
  }, [loading, isAuthenticated, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case "success":
        return (
          <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
        );
      case "error":
        return <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />;
      default:
        return (
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        );
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "success":
        return "text-emerald-400";
      case "error":
        return "text-red-400";
      default:
        return "text-white/70";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center brand-backdrop">
      <div className="text-center max-w-md mx-auto p-6">
        {getStatusIcon()}
        <h2 className="text-2xl font-bold text-white mb-2">
          {status === "processing" && "جاري معالجة تسجيل الدخول"}
          {status === "success" && "تم تسجيل الدخول بنجاح!"}
          {status === "error" && "فشل في تسجيل الدخول"}
        </h2>
        <p className={getStatusColor()}>{message}</p>

        {status === "processing" && (
          <div className="mt-6 text-sm text-white/50">
            <p>يرجى الانتظار بينما نكمل عملية المصادقة.</p>
            <p className="mt-2">قد يستغرق هذا بضع لحظات...</p>
          </div>
        )}

        {status === "success" && (
          <div className="mt-6 text-sm text-emerald-400">
            <p>✓ تم التحقق من حسابك</p>
            <p>✓ جاري التوجيه إلى البوابة...</p>
          </div>
        )}

        {status === "error" && (
          <div className="mt-6 text-sm text-red-400">
            <p>✗ لم يتم إكمال عملية المصادقة</p>
            <p>✗ جاري التوجيه إلى صفحة تسجيل الدخول...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Callback;
